/**
 * 验证辅助工具类（前端版本）
 * 统一管理数据验证逻辑，减少重复代码
 */
export default class ValidationHelper {
    /**
     * 验证对象是否存在且不为空
     * @param {*} value - 要验证的值
     * @param {string} name - 值名称（用于错误消息）
     * @returns {Object} {valid: boolean, message?: string}
     */
    static validateRequired(value, name = '参数') {
        if (value === null || value === undefined) {
            return {
                valid: false,
                message: `${name}不能为空`
            };
        }
        return { valid: true };
    }
    
    /**
     * 验证多个必需字段
     * @param {Object} data - 数据对象
     * @param {Array<string>} fields - 必需字段数组
     * @returns {Object} {valid: boolean, message?: string, missingFields?: Array<string>}
     */
    static validateRequiredFields(data, fields) {
        if (!data) {
            return {
                valid: false,
                message: '数据对象不能为空',
                missingFields: fields
            };
        }
        
        const missingFields = fields.filter(field => 
            data[field] === null || data[field] === undefined
        );
        
        if (missingFields.length > 0) {
            return {
                valid: false,
                message: `缺少必需字段: ${missingFields.join(', ')}`,
                missingFields: missingFields
            };
        }
        
        return { valid: true };
    }
    
    /**
     * 验证对象类型
     * @param {*} value - 要验证的值
     * @param {string} expectedType - 期望的类型（'object', 'array', 'string', 'number'等）
     * @param {string} name - 值名称（用于错误消息）
     * @returns {Object} {valid: boolean, message?: string}
     */
    static validateType(value, expectedType, name = '参数') {
        let actualType = typeof value;
        
        if (expectedType === 'array' && !Array.isArray(value)) {
            return {
                valid: false,
                message: `${name}必须是数组类型`
            };
        }
        
        if (expectedType === 'object' && (actualType !== 'object' || value === null || Array.isArray(value))) {
            return {
                valid: false,
                message: `${name}必须是对象类型`
            };
        }
        
        if (expectedType !== 'array' && expectedType !== 'object' && actualType !== expectedType) {
            return {
                valid: false,
                message: `${name}必须是${expectedType}类型`
            };
        }
        
        return { valid: true };
    }
    
    /**
     * 验证数值范围
     * @param {number} value - 要验证的数值
     * @param {Object} range - 范围 {min?: number, max?: number}
     * @param {string} name - 值名称（用于错误消息）
     * @returns {Object} {valid: boolean, message?: string}
     */
    static validateRange(value, range, name = '参数') {
        if (typeof value !== 'number') {
            return {
                valid: false,
                message: `${name}必须是数字类型`
            };
        }
        
        if (range.min !== undefined && value < range.min) {
            return {
                valid: false,
                message: `${name}不能小于${range.min}`
            };
        }
        
        if (range.max !== undefined && value > range.max) {
            return {
                valid: false,
                message: `${name}不能大于${range.max}`
            };
        }
        
        return { valid: true };
    }
    
    /**
     * 验证字符串长度
     * @param {string} value - 要验证的字符串
     * @param {Object} length - 长度限制 {min?: number, max?: number}
     * @param {string} name - 值名称（用于错误消息）
     * @returns {Object} {valid: boolean, message?: string}
     */
    static validateLength(value, length, name = '参数') {
        if (typeof value !== 'string') {
            return {
                valid: false,
                message: `${name}必须是字符串类型`
            };
        }
        
        const strLength = value.length;
        
        if (length.min !== undefined && strLength < length.min) {
            return {
                valid: false,
                message: `${name}长度不能少于${length.min}个字符`
            };
        }
        
        if (length.max !== undefined && strLength > length.max) {
            return {
                valid: false,
                message: `${name}长度不能超过${length.max}个字符`
            };
        }
        
        return { valid: true };
    }
    
    /**
     * 验证游戏数据对象（玩家、道具、装备等）
     * @param {Object} data - 数据对象
     * @param {string} type - 数据类型（'player', 'item', 'equipment'等）
     * @returns {Object} {valid: boolean, message?: string}
     */
    static validateGameData(data, type = 'gameData') {
        if (!data) {
            return {
                valid: false,
                message: `${type}数据不能为空`
            };
        }
        
        if (typeof data !== 'object') {
            return {
                valid: false,
                message: `${type}数据必须是对象类型`
            };
        }
        
        return { valid: true };
    }
    
    /**
     * 组合多个验证结果
     * @param {Array<Object>} validations - 验证结果数组 [{valid: boolean, message?: string}, ...]
     * @returns {Object} {valid: boolean, messages?: Array<string>}
     */
    static combineValidations(validations) {
        const failed = validations.filter(v => !v.valid);
        
        if (failed.length === 0) {
            return { valid: true };
        }
        
        return {
            valid: false,
            messages: failed.map(v => v.message).filter(m => m)
        };
    }
}

